import React, { useState, useEffect } from "react";
import { FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import products from "../data/products"; // Import product data
import ifus from "../data/ifuData"; // Import IFU data
import "../css/SearchBar.css";

const SearchBar = ({ setIsSearchActive }) => {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [overlayActive, setOverlayActive] = useState(false); // State for overlay
  const navigate = useNavigate();

  const handleInputChange = (event) => {
    const value = event.target.value;
    setQuery(value);

    if (value.trim() === "") {
      setSuggestions([]);
      return;
    }

    // Combine both arrays
    const allItems = [...products, ...ifus];

    const scoredItems = allItems
      .map((item) => {
        const name = item.name.toLowerCase();
        const queryLower = value.toLowerCase();
        let score = 0;

        // Exact match
        if (name === queryLower) {
          score = 100;
        }
        // Starts with query
        else if (name.startsWith(queryLower)) {
          score = 75;
        }
        // Whole word match
        else if (name.split(' ').includes(queryLower)) {
          score = 65;
        }
        // Contains query
        else if (name.includes(queryLower)) {
          score = 50;
        }

        // Boost IFUs slightly
        if (item.file) {
          score += 10; // this ensures IFUs rank above unrelated products
        }

        // Reduce length penalty slightly
        score -= name.length * 0.05;

        return { ...item, score };
      })
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score);

    setSuggestions(scoredItems);
  };

  const handleSelectSuggestion = (item) => {
    setQuery("");
    setSuggestions([]);
    setOverlayActive(false); // Remove overlay when a suggestion is selected
    setIsSearchActive(false); // Close search bar after clicking suggestion

    if (item.file) {
      const ifuIndex = ifus.findIndex((ifu) => ifu.file === item.file);
      const adjustedIndex = ifuIndex + 1;
      navigate(`/ifu/${adjustedIndex}`);
    } else {
      navigate(`/products/${item.id}`, { state: { image: item.img } });
    }
  };

  const handleSearch = () => {
    if (query.trim() !== "") {
      navigate(`/search?q=${encodeURIComponent(query)}`);
      setQuery("");
      setSuggestions([]);
      setOverlayActive(false); // Remove overlay after search
      // Hide the mobile keyboard by removing focus from input
      document.activeElement.blur();
    } else {
      alert("Enter a proper product or IFU.");
    }
  };

  // Activate overlay when search bar is clicked (only for desktop)
  const handleClick = () => {
    if (window.innerWidth > 892) {
      setOverlayActive(true);
    }
  };

  // Remove overlay when clicking outside
  const handleOverlayClick = () => {
    setOverlayActive(false);
    setSuggestions([]); // clear suggestions
    setQuery("");       // clear search text
  };

  // Handle Enter key press
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  // Attach event listener when overlay is active or search bar is focused
  useEffect(() => {
    if (overlayActive) {
      document.addEventListener("keydown", handleKeyDown);
    } else {
      document.removeEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [overlayActive]);

  return (
    <>
      {/* Overlay */}
      {overlayActive && <div className="search-overlay" onClick={handleOverlayClick}></div>}

      <div className="search-container">
        <input
          type="text"
          placeholder={"Search for products or IFUs..."}
          value={query}
          onChange={handleInputChange}
          onClick={handleClick} // Activate overlay on click
          onKeyDown={handleKeyDown} // Capture Enter key press
          className="search-bar"
        />
        <button className="search-button" onClick={handleSearch}>
          <FaSearch />
        </button>

        {suggestions.length > 0 && (
          <ul className="search-suggestions">
            {suggestions.map((item) => (
              <li key={item.id} onClick={() => handleSelectSuggestion(item)}>
                {item.file ? (
                  <>
                    <span className="search-ifu-symbol-container">📄</span> <span>{item.name}</span>
                  </>
                ) : (
                  <>
                    <img src={`/${item.img}`} alt={item.name} className="suggestion-image" />
                    <span>{item.name}</span>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
};

export default SearchBar;
